import React, { useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Alert,
} from "react-bootstrap";
import Container from "react-bootstrap/Container";
import { useTranslation } from "react-i18next";
import { SideBar } from "./sideBar";
import { NavBar } from "./navBar";
import { useAuth } from "../contexts/AuthContext";
import { FaCheckCircle, FaPaperclip, FaTimes } from "react-icons/fa";
import { createTicket } from "../api/apiClient";

const CATEGORIES = [
  { value: 0, labelKey: "report.categoryErrors" },
  { value: 1, labelKey: "report.categoryInquiries" },
  { value: 2, labelKey: "report.categorySupportRequest" },
  { value: 3, labelKey: "report.categoryOther" },
];

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 10;

export const MainReportProblem = () => {
  const { t } = useTranslation();
  const { clientId, userId } = useAuth();
  const fileInputRef = useRef(null);

  const hasPermissions = clientId !== null && clientId !== undefined;

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [validated, setValidated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('handleChange:', name, value, typeof value);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFileError("");
    const selected = Array.from(e.target.files);
    const remaining = MAX_FILES - files.length;

    if (selected.length > remaining) {
      setFileError(t("report.fileMaxCount", { count: MAX_FILES }));
      e.target.value = "";
      return;
    }

    const oversized = selected.find(
      (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024,
    );
    if (oversized) {
      setFileError(t("report.fileMaxSize", { size: MAX_FILE_SIZE_MB }));
      e.target.value = "";
      return;
    }

    setFiles((prev) => [
      ...prev,
      ...selected.map((f) => ({
        file: f,
        id: `${f.name}-${Date.now()}-${Math.random()}`,
        preview: f.type.startsWith("image/")
          ? URL.createObjectURL(f)
          : null,
      })),
    ]);

    e.target.value = "";
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Debug: Ver qué valor tiene formData.category
      console.log('formData.category antes de parsear:', formData.category, typeof formData.category);

      // Validar y parsear categoría
      const categoryValue = parseInt(formData.category, 10);
      console.log('categoryValue después de parsear:', categoryValue, typeof categoryValue);

      // Validar que la categoría sea válida (0-3)
      if (isNaN(categoryValue) || categoryValue < 0 || categoryValue > 3) {
        setError("Por favor selecciona una categoría válida");
        setLoading(false);
        return;
      }

      // Preparar datos del ticket
      const ticketData = {
        title: formData.title,
        category: categoryValue,
        description: formData.description,
        status: 0, // Nuevo
        priority: 1, // Media
        clientId: clientId || null,
        userId: userId || null,
      };

      // Preparar archivos
      const fileList = files.map((f) => f.file);

      // Enviar al API
      const response = await createTicket(ticketData, fileList);

      if (response.success) {
        setSubmitted(true);
      } else {
        setError(response.message || "Error al crear el ticket");
      }
    } catch (ex) {
      setError("Error inesperado al crear el ticket");
      console.error("Error creating ticket:", ex);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFormData({ title: "", category: "", description: "" });
    setFiles([]);
    setValidated(false);
    setSubmitted(false);
    setFileError("");
    setError("");
    setLoading(false);
  };

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Card>
              <Card.Header>
                <Card.Title as="h5">{t("report.pageTitle")}</Card.Title>
              </Card.Header>
              <Card.Body>
                {submitted ? (
                  /* Vista de éxito */
                  <Row className="justify-content-md-center">
                    <Col xs={12} md={8} className="text-center py-4">
                      <FaCheckCircle
                        size={56}
                        color="#1de9b6"
                        className="mb-3"
                      />
                      <h5 className="steps-color mb-2">
                        {t("report.successTitle")}
                      </h5>
                      <p className="text-muted mb-4">
                        {t("report.successMessage")}
                      </p>
                      <Button variant="primary" onClick={handleReset}>
                        {t("report.reportAnother")}
                      </Button>
                    </Col>
                  </Row>
                ) : (
                  <Form
                    className="addForm"
                    onSubmit={handleSubmit}
                    noValidate
                    validated={validated}
                  >
                    <Row className="justify-content-md-center">
                      <Col xs={12} md={10}>
                        <h5 className="steps-color">
                          {t("report.formSectionLabel")}
                        </h5>

                        {/* Título */}
                        <InputGroup className="mb-3">
                          <InputGroup.Text>
                            {t("report.titleLabel")}
                          </InputGroup.Text>
                          <Form.Control
                            required
                            minLength={5}
                            maxLength={100}
                            type="text"
                            name="title"
                            value={formData.title}
                            placeholder={t("report.titlePlaceholder")}
                            onChange={handleChange}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t("report.titleRequired")}
                          </Form.Control.Feedback>
                        </InputGroup>

                        {/* Categoría */}
                        <InputGroup className="mb-3">
                          <InputGroup.Text>
                            {t("report.categoryLabel")}
                          </InputGroup.Text>
                          <Form.Select
                            required
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                          >
                            <option value="">
                              {t("report.categoryPlaceholder")}
                            </option>
                            {CATEGORIES.map((cat) => (
                              <option key={cat.value} value={cat.value}>
                                {t(cat.labelKey)}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {t("report.categoryRequired")}
                          </Form.Control.Feedback>
                        </InputGroup>

                        {/* Descripción */}
                        <InputGroup className="mb-3">
                          <InputGroup.Text>
                            {t("report.descriptionLabel")}
                          </InputGroup.Text>
                          <Form.Control
                            as="textarea"
                            required
                            minLength={10}
                            maxLength={1000}
                            rows={5}
                            name="description"
                            value={formData.description}
                            placeholder={t("report.descriptionPlaceholder")}
                            onChange={handleChange}
                            style={{ resize: "none" }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t("report.descriptionRequired")}
                          </Form.Control.Feedback>
                        </InputGroup>
                        <div
                          className="text-end mb-3"
                          style={{ fontSize: "0.8rem", color: "#6c757d" }}
                        >
                          {formData.description.length} / 1000
                        </div>

                        {/* Archivos de evidencia */}
                        <InputGroup className="mb-2">
                          <InputGroup.Text>
                            {t("report.filesLabel")}
                          </InputGroup.Text>
                          <Form.Control
                            type="file"
                            multiple
                            accept="image/*,.pdf,.doc,.docx,.txt"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            disabled={files.length >= MAX_FILES}
                          />
                        </InputGroup>
                        <div
                          className="text-muted mb-2"
                          style={{ fontSize: "0.8rem" }}
                        >
                          {t("report.filesHint", {
                            count: MAX_FILES,
                            size: MAX_FILE_SIZE_MB,
                          })}
                        </div>

                        {/* Error de archivos */}
                        {fileError && (
                          <div
                            className="text-danger mb-2"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {fileError}
                          </div>
                        )}

                        {/* Lista de archivos adjuntos */}
                        {files.length > 0 && (
                          <div className="mb-3 d-flex flex-wrap gap-2">
                            {files.map((item) => (
                              <div
                                key={item.id}
                                className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                                style={{
                                  maxWidth: "220px",
                                  backgroundColor: "#f8f9fa",
                                }}
                              >
                                {item.preview ? (
                                  <img
                                    src={item.preview}
                                    alt=""
                                    style={{
                                      width: "28px",
                                      height: "28px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                    }}
                                  />
                                ) : (
                                  <FaPaperclip
                                    size={14}
                                    className="text-muted"
                                  />
                                )}
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                  }}
                                >
                                  {item.file.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(item.id)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "0",
                                    color: "#dc3545",
                                    display: "flex",
                                  }}
                                >
                                  <FaTimes size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {files.length > 0 && (
                          <div
                            className="text-muted mb-3"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {files.length}/{MAX_FILES}{" "}
                            {t("report.filesAttached")}
                          </div>
                        )}

                        {/* Error message */}
                        {error && (
                          <Alert variant="danger" className="mb-3">
                            {error}
                          </Alert>
                        )}
                      </Col>
                    </Row>

                    {/* Botones */}
                    <div className="d-flex justify-content-center gap-2">
                      <Button
                        variant="secondary"
                        onClick={handleReset}
                        style={{ width: "150px" }}
                        disabled={loading}
                      >
                        {t("report.cancelButton")}
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        style={{ width: "150px" }}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {t("report.submittingButton") || "Enviando..."}
                          </>
                        ) : (
                          t("report.submitButton")
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Row>
        </Container>
      </Container>
    </React.Fragment>
  );
};
